import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { CornerDownRight, Pencil, Check, X } from 'lucide-react';
import type { ConversationItem } from '@/hooks/use-conversations-v2';
import { useRenameConversation } from '@/hooks/use-conversations-v2';
import { usePublicDeal } from '@/hooks/use-public-deal';
import { formatRelativeTime } from '@/lib/format';

interface ConversationSidebarProps {
  conversations: ConversationItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  /** When true, disables the "+ New Analysis" CTA (billing read-only mode). */
  createDisabled?: boolean;
  /** Tooltip / title shown on hover when `createDisabled` is true. */
  createDisabledReason?: string;
}

function ForkAttribution({ shareId, createdAt }: { shareId: string; createdAt: string }) {
  const { data: parentDeal } = usePublicDeal(shareId);
  const ownerName = parentDeal?.ownerName ?? null;
  const relTime = (() => {
    try { return formatRelativeTime(createdAt); } catch { return null; }
  })();
  const text = ownerName
    ? `forked from ${ownerName}${relTime ? ` · ${relTime}` : ''}`
    : `forked deal${relTime ? ` · ${relTime}` : ''}`;
  return (
    <span className="text-micro font-mono text-text-muted truncate" data-testid="fork-attribution">
      {text}
    </span>
  );
}

export function ConversationSidebar({ conversations, activeId, onSelect, onCreate, createDisabled = false, createDisabledReason }: ConversationSidebarProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const rename = useRenameConversation();

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const startRename = (id: string, currentTitle: string) => {
    setEditingId(id);
    setEditTitle(currentTitle);
  };

  const confirmRename = () => {
    if (editingId && editTitle.trim()) {
      rename.mutate({ id: editingId, title: editTitle.trim() });
    }
    setEditingId(null);
  };

  const cancelRename = () => {
    setEditingId(null);
  };

  return (
    <div
      data-testid="conversation-sidebar"
      className="w-72 2xl:w-80 bg-void border-r border-panel-border flex flex-col shrink-0"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-panel-border flex items-center justify-between">
        <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.2px]">
          CONVERSATIONS
        </span>
        <span className="text-sm font-mono text-text-dim tabular-nums">{conversations.length}</span>
      </div>

      {/* New Analysis CTA */}
      <div className="p-3">
        <button
          onClick={createDisabled ? undefined : onCreate}
          disabled={createDisabled}
          title={createDisabled ? createDisabledReason : undefined}
          data-testid="conversation-sidebar-create"
          className={clsx(
            'w-full py-3 border rounded-[2px] text-sm font-mono font-semibold tracking-[0.3px] transition-all duration-200',
            createDisabled
              ? 'bg-panel-bg border-panel-border text-text-dim cursor-not-allowed'
              : 'bg-accent-dim border-accent/15 text-accent cursor-pointer hover:border-accent/27 hover:bg-accent/13',
          )}
        >
          + New Analysis
        </button>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto px-2.5">
        {conversations.map((c) => {
          const isActive = c.id === activeId;
          const isComplete = c.status === 'COMPLETE';
          const isEditing = editingId === c.id;
          const isFork = !!c.forkedFromShareId;

          return (
            <div
              key={c.id}
              data-testid="conversation-row"
              data-fork={isFork ? 'true' : 'false'}
              className={clsx(
                'w-full py-3 px-3 flex flex-col gap-1.5 transition-all duration-100 group',
                isActive
                  ? 'bg-accent-faint border-l-2 border-l-accent'
                  : 'bg-transparent border-l-2 border-l-transparent',
              )}
            >
              <div className="flex items-center gap-1.5">
                {/* Status Icon */}
                {isComplete ? (
                  <span className="w-3.5 h-3.5 rounded-full bg-accent-dim border border-accent/20 flex items-center justify-center text-xs text-accent shrink-0">
                    &#10003;
                  </span>
                ) : isFork ? (
                  <span
                    className="w-3.5 h-3.5 flex items-center justify-center text-blue shrink-0"
                    title="Forked from a shared deal"
                    aria-label="Forked deal"
                  >
                    <CornerDownRight size={12} aria-hidden="true" />
                  </span>
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full bg-input-bg border border-panel-border shrink-0" />
                )}

                {/* Name — editable or clickable */}
                {isEditing ? (
                  <div className="flex-1 flex items-center gap-1">
                    <input
                      ref={inputRef}
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') confirmRename();
                        if (e.key === 'Escape') cancelRename();
                      }}
                      className="flex-1 bg-input-bg border border-accent/30 rounded-[2px] px-2 py-0.5 text-sm font-body text-text-bright outline-none"
                    />
                    <button onClick={confirmRename} className="text-accent cursor-pointer bg-transparent border-none p-0.5">
                      <Check size={12} />
                    </button>
                    <button onClick={cancelRename} className="text-text-dim cursor-pointer bg-transparent border-none p-0.5">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => onSelect(c.id)}
                    className={clsx(
                      'flex-1 text-sm font-body text-left overflow-hidden text-ellipsis whitespace-nowrap bg-transparent border-none cursor-pointer p-0',
                      isActive ? 'text-text-bright font-medium' : 'text-text font-normal',
                    )}
                  >
                    {c.title}
                  </button>
                )}

                {/* Rename button — visible on hover or when active */}
                {!isEditing && (
                  <button
                    onClick={(e) => { e.stopPropagation(); startRename(c.id, c.title); }}
                    className="opacity-0 group-hover:opacity-100 text-text-dim hover:text-text-muted cursor-pointer bg-transparent border-none p-0.5 transition-opacity"
                    title="Rename"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
              {isFork && c.forkedFromShareId && (
                <div className="ml-5">
                  <ForkAttribution shareId={c.forkedFromShareId} createdAt={c.createdAt} />
                </div>
              )}
              <div className="flex items-center gap-1.5 ml-5">
                {/* Status Badge */}
                <span
                  className={clsx(
                    'text-micro font-mono font-semibold tracking-[0.5px] px-1.5 py-px rounded-[2px] border',
                    isComplete
                      ? 'text-accent bg-accent-dim border-accent/15'
                      : 'text-warn bg-warn-dim border-warn/15',
                  )}
                >
                  {c.status}
                </span>
                {/* Version Badge */}
                {c.modelVersion && (
                  <span className="text-micro font-mono text-text-dim tracking-[0.5px]">
                    v{c.modelVersion}
                  </span>
                )}
                {/* Date */}
                <span className="text-xs text-text-dim font-mono">
                  {new Date(c.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
